import { Middleware } from '@reduxjs/toolkit'
import { WbfPolicies } from '@workers-benefit-fund/shared-data'

import { BENESTREAM_ORG_ID } from 'src/common/config'
import { cleanStringToBeUrlReadable } from 'src/default/helpers/clean-string'
import { navigationState } from 'src/v2/commons/utils/navigation'
import { isCookieAuthenticationEnabled } from 'src/v2/domains/auth/utils/isCookieAuthenticationEnabled'

import { RootState } from '../store'

import type { PendingPasskeyStep } from '@v2/domains/auth/auth.types'
import { rememberPendingAuthenticatedLocation } from '@v2/domains/organization/utils/organizationNavigationSession.utils'
import {
  checkRoute,
  navigateToRoute,
} from '@v2/domains/routing/actions/routing.actions'
import {
  ALL_ROUTES,
  PUBLIC_ROUTES_CONFIG,
} from '@v2/domains/routing/constants/route-configs.constants'
import { RouteKey } from '@v2/domains/routing/types/routing.types'
import { isPublicRoute } from '@v2/domains/routing/utils/route-guards.utils'
import { buildRoute } from '@v2/domains/routing/utils/route.utils'
import { IUserSessionStore } from '@v2/domains/user/user.types'

const ENROLLMENT_CHECKER_PATH = '/enrollment-checker'
const LEADS_CHECKER_PATH = '/leads'

const routerMiddleware: Middleware = (store) => (next) => (action) => {
  const state = store.getState() as RootState
  const userSession = state.user.userSession
  const { accessToken, status, pendingPasskeyStep } = state.auth
  const isAuthenticationPending =
    isCookieAuthenticationEnabled && status === 'unknown'
  const isAuthenticated = isCookieAuthenticationEnabled
    ? status === 'authenticated'
    : Boolean(accessToken)

  if (navigateToRoute.match(action)) {
    return handleNavigateToRoute(action, userSession, next)
  }

  if (checkRoute.match(action)) {
    const result = next(action)

    if (!action.payload?.skipValidation && !isAuthenticationPending) {
      handleCheckRoute(userSession, isAuthenticated, pendingPasskeyStep)
    }
    return result
  }

  return next(action)
}

const handleNavigateToRoute = (
  action: ReturnType<typeof navigateToRoute>,
  userSession: IUserSessionStore,
  next: (action: unknown) => unknown,
) => {
  const { routeKey, params, options = {} } = action.payload
  const {
    includeOrgPrefix = true,
    hardRedirect = false,
    replace = false,
  } = options

  const routeConfig = ALL_ROUTES[routeKey]
  if (!routeConfig) {
    return next(action)
  }

  const fullPath = buildRoute(
    routeKey as RouteKey,
    params,
    userSession.currentOrganization?.name,
    includeOrgPrefix,
  )

  if (hardRedirect) {
    if (replace) {
      window.location.replace(fullPath)
    } else {
      window.location.href = fullPath
    }
    return next(action)
  }

  if (navigationState.navigate) {
    navigationState.navigate(fullPath, { replace })
  } else if (replace) {
    navigationState.replace(fullPath)
  } else {
    window.location.href = fullPath
  }

  return next(action)
}

const handleCheckRoute = (
  userSession: IUserSessionStore,
  isAuthenticated: boolean,
  pendingPasskeyStep: PendingPasskeyStep | null,
) => {
  if (!navigationState.location) {
    return
  }

  const pathname = navigationState.location.pathname

  // A required passkey step must finish before any other route opens.
  if (isAuthenticated && pendingPasskeyStep) {
    const stepPath = PUBLIC_ROUTES_CONFIG[pendingPasskeyStep].path
    if (pathname !== stepPath) navigate(stepPath)
    return
  }
  const { policies } = userSession
  localStorage.setItem('lastCheckedRoute', pathname)
  const isEnrollmentCheckerVendorsAllowed = policies.includes(
    WbfPolicies.is_enrollment_checker_vendors_enabled,
  )

  if (pathname === '/') {
    if (isAuthenticated) {
      if (userSession.currentOrganization?.id) {
        if (userSession.currentOrganization.id === BENESTREAM_ORG_ID) {
          document.location.href = '/benestream/leads'
          return
        }

        const baseUrl = `/${cleanStringToBeUrlReadable(
          userSession.currentOrganization.name,
        )}`
        const path = isEnrollmentCheckerVendorsAllowed
          ? ENROLLMENT_CHECKER_PATH
          : LEADS_CHECKER_PATH
        navigate(baseUrl + path)
        return
      }
      navigate('/select-organization')
      return
    }
    navigate('/login')
    return
  }

  const isPublic = isPublicRoute(pathname)

  if (!isAuthenticated && !isPublic) {
    rememberPendingAuthenticatedLocation(
      `${pathname}${navigationState.location.search}${navigationState.location.hash}`,
    )
    navigate('/login')
    return
  }

  if (
    isAuthenticated &&
    !userSession.currentOrganization.id &&
    !pathname.includes('select-organization') &&
    !isPublic
  ) {
    navigate('/select-organization')
    return
  }

  if (
    isAuthenticated &&
    userSession.currentOrganization.id &&
    (pathname.includes('login') || pathname.includes('select-organization'))
  ) {
    if (userSession.currentOrganization.id === BENESTREAM_ORG_ID) {
      document.location.href = '/benestream/leads'
      return
    }

    const path = `/${cleanStringToBeUrlReadable(
      userSession.currentOrganization.name,
    )}/leads`
    navigate(path)
    return
  }
}

const navigate = (path: string) => {
  if (navigationState.navigate) {
    navigationState.navigate(path)
  } else {
    console.warn('navigationState.navigate is not avaialbe')
    window.location.href = path
  }
}

export default routerMiddleware
